import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown, User, Lock, LogOut, UserPlus, ArrowLeftRight,
  Trash2, Loader2, Shield, ImagePlus,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAccountSwitcher } from '../hooks/useAccountSwitcher';
import { MAX_ACCOUNTS } from '../lib/multiAccountStore';
import { coverUrlForUser } from '../lib/avatar';
import Avatar from './Avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Premium multi-account menu (2026-07-26).
 *
 * Anatomy — top → bottom:
 *   1. Active account card       — big avatar (with camera badge for
 *      photo upload), full name, email + role chip.
 *   2. "Other accounts" list      — every other stored session with a
 *      "switch" button and a trash icon.
 *   3. "+ Add another account"    — primary CTA that snapshots the
 *      current session then routes to /login for the new one.
 *   4. Utility footer             — Profile, Change Password, Sign out.
 *
 * Uses `useAccountSwitcher` for the multi-account plumbing so ALL the
 * data-isolation guarantees (queryClient.clear + IDB purge + token
 * rotation) apply for free.
 */
export const ProfileDropdown = () => {
  const { user, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const {
    accounts, activeUserId, isSwitching,
    addAccount, switchToAccount, removeAccount,
  } = useAccountSwitcher();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const otherAccounts = accounts.filter((a) => a.user_id !== activeUserId);
  const rtl = language === 'ar';
  const align = rtl ? 'left-0' : 'right-0';

  // After a fresh avatar upload we need /auth/me to hydrate so the
  // menu, header, and every downstream consumer see the new url.
  const handleAvatarUploaded = async () => {
    try { await checkAuth(); } catch (_e) { /* ignore */ }
  };

  // Aug 2026 — profile cover background (الخلفية) upload.
  const coverInputRef = useRef(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const handleCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      alert('حجم الخلفية يتجاوز 4MB — يرجى تصغيرها.');
      return;
    }
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/users/me/cover`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await checkAuth();
    } catch (err) {
      alert(err?.response?.data?.detail || 'فشل رفع الخلفية، حاول مرة أخرى.');
    } finally {
      setCoverBusy(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };
  const userCover = user?.cover_url
    ? coverUrlForUser(user?.id || user?._id, user?.cover_version)
    : null;

  const handleSignOut = async () => {
    // Sign out uses `removeAccount(activeUserId)` so other stored
    // accounts stay accessible in the switcher. If none remain,
    // the hook navigates to /login automatically.
    if (activeUserId) await removeAccount(activeUserId);
    else navigate('/login', { replace: true });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 text-white hover:bg-white/10 rounded-sm transition-colors"
        data-testid="profile-dropdown-btn"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Avatar
          userId={user?.id || user?._id}
          name={user?.full_name}
          avatarUrl={user?.avatar_url}
          avatarVersion={user?.avatar_version}
          size={32}
        />
        <span className="hidden sm:flex flex-col text-start leading-tight">
          <span className="text-sm font-semibold truncate max-w-[140px]">
            {user?.full_name || t('User', 'مستخدم')}
          </span>
          <span className="text-[10px] text-[#9CB0CC]">{user?.role}</span>
        </span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={`absolute z-50 ${align} mt-2 w-[340px] max-w-[92vw] bg-white border border-slate-200 rounded-xl shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] overflow-hidden`}
          data-testid="profile-dropdown-menu"
          data-keep-light
          dir={rtl ? 'rtl' : 'ltr'}
          role="menu"
        >
          {/* ── Active account card (with cover الخلفية) ─────────── */}
          <div
            className="relative px-5 pt-5 pb-4 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0A1128 0%, #141E3D 55%, #0A1128 100%)',
              color: '#FFFFFF',
            }}
            data-testid="active-account-card"
          >
            {userCover && (
              <>
                <img
                  src={userCover}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="active-account-cover"
                />
                <div className="absolute inset-0 bg-[#0A1128]/65" />
              </>
            )}
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              disabled={coverBusy}
              className="absolute top-2 end-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full
                         bg-black/40 backdrop-blur text-[10px] text-white/85 hover:text-white
                         hover:bg-black/60 transition-colors disabled:opacity-50"
              data-testid="cover-edit-btn"
              aria-label={t('Change cover', 'تغيير الخلفية')}
            >
              {coverBusy ? <Loader2 size={11} className="animate-spin" /> : <ImagePlus size={11} />}
              {t('Cover', 'الخلفية')}
            </button>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleCoverFile}
              data-testid="cover-file-input"
            />
            <div className="relative flex items-center gap-4">
              <Avatar
                userId={user?.id || user?._id}
                name={user?.full_name}
                avatarUrl={user?.avatar_url}
                avatarVersion={user?.avatar_version}
                size={64}
                editable
                onUploaded={handleAvatarUploaded}
              />
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold truncate leading-tight">
                  {user?.full_name || t('User', 'مستخدم')}
                </p>
                <p className="text-xs text-white/70 truncate mt-0.5">
                  {user?.email || user?.phone_number || ''}
                </p>
                {user?.role && (
                  <span
                    data-testid="active-role-chip"
                    className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-white/15 rounded-full text-[10px] font-medium tracking-wide"
                  >
                    <Shield size={10} />
                    {user.role}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Other accounts ──────────────────────────────────── */}
          {otherAccounts.length > 0 && (
            <div
              className="px-3 pt-3 border-b border-slate-100"
              data-testid="other-accounts-section"
            >
              <p className="px-2 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                {t('Other accounts', 'حسابات أخرى')}
              </p>
              <ul className="space-y-1">
                {otherAccounts.map((a) => (
                  <li
                    key={a.user_id}
                    data-testid={`other-account-${a.user_id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <Avatar
                      userId={a.user_id}
                      name={a.full_name}
                      avatarUrl={a.avatar}
                      avatarVersion={null}
                      size={36}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {a.full_name || '—'}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {a.email || a.user_id}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => switchToAccount(a.user_id)}
                      disabled={isSwitching}
                      data-testid={`switch-account-${a.user_id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md bg-[#0A1128] text-white hover:bg-[#141E3D] disabled:opacity-50 transition-colors"
                      aria-label={t('Switch', 'تبديل')}
                    >
                      {isSwitching ? <Loader2 size={11} className="animate-spin" /> : <ArrowLeftRight size={11} />}
                      {t('Switch', 'تبديل')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeAccount(a.user_id); }}
                      data-testid={`remove-account-${a.user_id}`}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      aria-label={t('Remove account', 'إزالة الحساب')}
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Add-another-account CTA ─────────────────────────── */}
          <button
            type="button"
            onClick={() => { setOpen(false); addAccount(); }}
            disabled={isSwitching || accounts.length >= MAX_ACCOUNTS}
            data-testid="add-another-account-btn"
            className="w-full flex items-center gap-3 px-5 py-3 text-sm font-semibold text-[#0A1128] bg-gradient-to-r from-amber-50 to-white hover:from-amber-100 disabled:opacity-50 border-b border-slate-100 transition-colors"
          >
            <span className="w-8 h-8 rounded-full bg-amber-400 text-[#0A1128] flex items-center justify-center">
              <UserPlus size={16} />
            </span>
            <span className="flex-1 text-start">
              {t('Add another account', 'إضافة حساب آخر')}
            </span>
            {accounts.length >= MAX_ACCOUNTS && (
              <span className="text-[10px] text-red-600 font-medium">
                {t('Max 5', 'الحد 5')}
              </span>
            )}
          </button>

          {/* ── Utility footer ──────────────────────────────────── */}
          <div className="py-1">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="profile-menu-view"
            >
              <User size={15} className="text-slate-500" />
              {t('Profile info', 'معلومات الملف الشخصي')}
            </Link>
            <Link
              to="/change-password"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="profile-menu-password"
            >
              <Lock size={15} className="text-slate-500" />
              {t('Change password', 'تغيير كلمة المرور')}
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={isSwitching}
              className="flex items-center gap-3 px-5 py-2.5 text-sm text-red-700 hover:bg-red-50 w-full border-t border-slate-100 disabled:opacity-50"
              data-testid="profile-menu-logout"
            >
              <LogOut size={15} />
              {t('Sign out', 'تسجيل الخروج')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
